// vendorTenderSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
    fetchVendorTenders,
    fetchTenderDetail,
    fetchTenderCounts,
    registerTender,
    updateRegistrationStatus,
    checkRegistrationStatus,
    fetchKpiData,
    fetchActionItems
} from "../tenderoperational/vendorTenderThunk";

const initialState = {
    focusTender: {
        id: "",
        title: "",
        badge: "",
        slaAlert: "",
        timeline: [],
        description: "",
        ursFile: {
            name: "",
            size: "",
            uploadedAt: ""
        }
    },

    stages: [],
    tenders: [],
    loading: false,
    error: null,

    // NEW: Registration state
    registration: {
        status: 'idle', // idle | loading | success | error
        data: null,
        error: null,
        registrationId: null,
        isRegistered: false
    },

    tenderCounts: {
        available: 0,
        followed: 0,
        won: 0,
        total: 0
    },
    activeFilter: 'available',

    // NEW: Upload progress
    uploadProgress: 0,

    kpiData: {
        totalFollowed: 0,
        pending: 0,
        won: 0,
        successRate: '0%'
    },
    actionItems: [],
};

const vendorTenderSlice = createSlice({

    name: "vendorTender",
    initialState,
    reducers: {
        // NEW: Reset registration status
        resetRegistrationStatus: (state) => {
            state.registration.status = 'idle';
            state.registration.error = null;
            state.registration.data = null;
            state.uploadProgress = 0;
        },

        // NEW: Set upload progress
        setUploadProgress: (state, action) => {
            state.uploadProgress = action.payload;
        },

        // NEW: Clear error
        clearError: (state) => {
            state.error = null;
            state.registration.error = null;
        },

        setActiveFilter: (state, action) => {
            state.activeFilter = action.payload;
        },
        
        setTenderCounts: (state, action) => {
            state.tenderCounts = action.payload;
        },

        setActionItems: (state, action) => {
            state.actionItems = action.payload;
        }
    },

    extraReducers: (builder) => {
        builder
            // Fetch Vendor Tenders
            .addCase(fetchVendorTenders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVendorTenders.fulfilled, (state, action) => {
                state.loading = false;
                state.tenders = action.payload;
            })
            .addCase(fetchVendorTenders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchTenderCounts.fulfilled, (state, action) => {
                state.tenderCounts = action.payload;
            })

            // Fetch Tender Detail
            .addCase(fetchTenderDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTenderDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.focusTender = action.payload;
                state.stages = action.payload.stages ?? [];
            })
            .addCase(fetchTenderDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // NEW: Register Tender
            .addCase(registerTender.pending, (state) => {
                state.registration.status = 'loading';
                state.registration.error = null;
                state.uploadProgress = 0;
            })
            .addCase(registerTender.fulfilled, (state, action) => {
                state.registration.status = 'success';
                state.registration.data = action.payload;
                state.registration.registrationId = action.payload?.id || null;
                state.registration.isRegistered = true;
                state.uploadProgress = 100;

                // Update focusTender jika ada
                if (state.focusTender && state.focusTender.id === action.payload?.tenderId) {
                    state.focusTender.isRegistered = true;
                    state.focusTender.registrationId = action.payload?.id;
                }
            })
            .addCase(registerTender.rejected, (state, action) => {
                state.registration.status = 'error';
                state.registration.error = action.payload;
                state.uploadProgress = 0;
            })

            // NEW: Update Registration Status
            .addCase(updateRegistrationStatus.pending, (state) => {
                state.registration.status = 'loading';
            })
            .addCase(updateRegistrationStatus.fulfilled, (state, action) => {
                state.registration.status = 'success';
                state.registration.data = action.payload;
                if (state.focusTender) {
                    state.focusTender.registrationStatus = action.payload?.status;
                }
            })
            .addCase(updateRegistrationStatus.rejected, (state, action) => {
                state.registration.status = 'error';
                state.registration.error = action.payload;
            })

            // NEW: Check Registration Status
            .addCase(checkRegistrationStatus.fulfilled, (state, action) => {
                state.registration.isRegistered = action.payload?.isRegistered || false;
                state.registration.data = action.payload;
                if (state.focusTender) {
                    state.focusTender.isRegistered = action.payload?.isRegistered || false;
                    state.focusTender.registrationStatus = action.payload?.status;
                }
            })
            .addCase(checkRegistrationStatus.rejected, (state, action) => {
                state.registration.isRegistered = false;
                state.registration.error = action.payload;
            })

            .addCase(fetchKpiData.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchKpiData.fulfilled, (state, action) => {
                state.loading = false;
                state.kpiData = action.payload;
            })
            .addCase(fetchKpiData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchActionItems.fulfilled, (state, action) => {
                state.actionItems = action.payload;
            })
            .addCase(fetchActionItems.rejected, (state, action) => {
                state.error = action.payload;
            });
    }

});

// NEW: Export actions
export const {
    resetRegistrationStatus,
    setUploadProgress,
    setActiveFilter,
    clearError
} = vendorTenderSlice.actions;

export default vendorTenderSlice.reducer;